import { useEffect, useRef, useState, type FormEvent } from "react";
import type {
  Merchant,
  PaymentOrder,
  RedPacket,
  PacketClaim,
} from "@jcqb/core";
import { request, submitOrder } from "../api";
import { CopyText, Header, WalletId } from "../components/ui";
import { PinPad } from "../components/PinPad";
const money = (n: number) =>
  (n / 1e6).toLocaleString("zh-CN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
const date = (n: number) =>
  new Date(n).toLocaleString("zh-CN", { hour12: false });
const labels: Record<string, string> = {
  pending: "待审核",
  active: "已开通",
  rejected: "已驳回",
  suspended: "已停用",
  paid: "已付款",
  expired: "已过期",
  open: "领取中",
  finished: "已领完",
  delivered: "已送达",
  sending: "发送中",
  failed: "待处理",
};
type PublicMerchant = Omit<Merchant, "secretBox"> & { hasApiKey: boolean };
type OrderView = Omit<PaymentOrder, "amount"> & {
  amount: string;
  payUrl: string;
  callback: string | null;
};
function ErrorText({ value }: { value: string }) {
  return value ? (
    <p className="commerce-error" role="alert">
      {value}
    </p>
  ) : null;
}
export function MerchantPage({ walletId }: { walletId: string }) {
  const [data, setData] = useState<{
      merchant: PublicMerchant | null;
      orders: OrderView[];
    } | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [pin, setPin] = useState(false),
    [key, setKey] = useState<{ merchantId: string; secret: string } | null>(
      null,
    );
  const [form, setForm] = useState({
    name: "",
    website: "",
    callbackUrl: "",
    contact: "",
    description: "",
  });
  const load = () =>
    request<{ merchant: PublicMerchant | null; orders: OrderView[] }>(
      "/merchant",
    )
      .then((r) => {
        setData(r);
        if (r.merchant) setForm(r.merchant);
      })
      .catch((e) => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await request("/merchant/apply", {
        method: "POST",
        body: JSON.stringify(form),
      });
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const merchant = data?.merchant;
  return (
    <div className="app">
      <Header title="商户中心" />
      <WalletId id={walletId} />
      <div className="body commerce-page">
        <section className="commerce-hero">
          <span>🏪</span>
          <div>
            <h2>收款，从一个链接开始</h2>
            <p>API 下单 · 聊天付款 · 自动通知</p>
          </div>
        </section>
        <ErrorText value={error} />
        {!data ? (
          <p>正在加载商户资料…</p>
        ) : (
          <>
            {merchant && (
              <section className="commerce-card">
                <div className="commerce-card-head">
                  <strong>{merchant.name}</strong>
                  <span className={`commerce-state ${merchant.status}`}>
                    {labels[merchant.status]}
                  </span>
                </div>
                {merchant.reason && <p>{merchant.reason}</p>}
                <p className="sub">商户 ID：{merchant.id}</p>
              </section>
            )}
            {!merchant || ["pending", "rejected"].includes(merchant.status) ? (
              <form onSubmit={submit} className="commerce-form">
                <h3>{merchant ? "完善入驻资料" : "申请商户入驻"}</h3>
                <p className="sub">
                  提交业务信息，由管理员审核收款资格与回调地址。
                </p>
                {[
                  {
                    key: "name",
                    label: "商户名称",
                    placeholder: "展示给付款人的商户名称",
                  },
                  {
                    key: "website",
                    label: "业务网站",
                    placeholder: "https://your-domain.com",
                    type: "url",
                  },
                  {
                    key: "callbackUrl",
                    label: "付款通知地址",
                    placeholder: "https://your-domain.com/payment/notify",
                    type: "url",
                  },
                  {
                    key: "contact",
                    label: "联系方式",
                    placeholder: "Telegram 用户名或邮箱",
                  },
                ].map((field) => (
                  <label key={field.key}>
                    {field.label}
                    <input
                      required
                      maxLength={
                        field.key === "name"
                          ? 60
                          : field.key === "contact"
                            ? 120
                            : 1000
                      }
                      type={field.type ?? "text"}
                      placeholder={field.placeholder}
                      value={form[field.key as keyof typeof form]}
                      onChange={(e) =>
                        setForm({ ...form, [field.key]: e.target.value })
                      }
                    />
                  </label>
                ))}
                <label>
                  业务介绍
                  <textarea
                    required
                    maxLength={500}
                    placeholder="说明商品或服务、收款用途"
                    value={form.description}
                    onChange={(e) =>
                      setForm({ ...form, description: e.target.value })
                    }
                  />
                </label>
                <button className="primary" disabled={busy}>
                  {busy
                    ? "提交中…"
                    : merchant
                      ? "更新并提交审核"
                      : "提交入驻申请"}
                </button>
              </form>
            ) : (
              <section className="commerce-card">
                <h3>接入收款</h3>
                <p>
                  支付成功后，资金直接进入您的钱包余额，可在钱包中申请提现。当前平台收单手续费为
                  0。
                </p>
                <div className="commerce-step">
                  <b>1</b>
                  <span>生成密钥，接入商户 API</span>
                </div>
                <div className="commerce-step">
                  <b>2</b>
                  <span>创建订单，将付款链接发送给客户</span>
                </div>
                <div className="commerce-step">
                  <b>3</b>
                  <span>验证付款回调，完成商品或服务交付</span>
                </div>
                <a
                  href="/merchant-api.html"
                  target="_blank"
                  rel="noreferrer"
                  className="commerce-link"
                >
                  查看 API 接入文档 ↗
                </a>
                <button
                  className="primary"
                  disabled={merchant.status !== "active" || busy}
                  onClick={() => {
                    setError("");
                    setPin(true);
                  }}
                >
                  {merchant.hasApiKey ? "重置 API 密钥" : "生成 API 密钥"}
                </button>
                {merchant.hasApiKey && (
                  <p className="sub">
                    重置后旧密钥立即失效，回调使用最新密钥签名。
                  </p>
                )}
              </section>
            )}
            {key && (
              <section className="commerce-card key-card">
                <h3>请立即保存接口密钥</h3>
                <p>密钥仅本次显示，请交给您的服务端妥善保存。</p>
                <CopyText value={key.secret} />
                <button className="ghost" onClick={() => setKey(null)}>
                  已保存，关闭显示
                </button>
              </section>
            )}
            <section className="commerce-card">
              <div className="commerce-card-head">
                <h3>最近收款订单</h3>
                <button className="ghost" onClick={() => void load()}>
                  刷新
                </button>
              </div>
              {!data.orders.length ? (
                <p className="commerce-empty">还没有收款订单</p>
              ) : (
                data.orders.map((o) => (
                  <div className="commerce-record" key={o.id}>
                    <div>
                      <strong>{o.title}</strong>
                      <span>{o.merchantOrderId}</span>
                      <small>{date(o.createdAt)}</small>
                    </div>
                    <div>
                      <strong>{o.amount} USDT</strong>
                      <span>
                        {o.status === "pending" ? "待付款" : labels[o.status]}
                      </span>
                      {o.callback && (
                        <small>
                          回调：
                          {o.callback === "pending"
                            ? "等待重试"
                            : labels[o.callback]}
                        </small>
                      )}
                    </div>
                  </div>
                ))
              )}
            </section>
          </>
        )}
      </div>
      {pin && (
        <PinPad
          title="验证安全密码"
          subtitle="验证后将生成新的商户 API 密钥"
          onCancel={() => setPin(false)}
          onSubmit={async (value) => {
            if (busy) return;
            setPin(false);
            setBusy(true);
            setError("");
            try {
              setKey(
                await request("/merchant/key", {
                  method: "POST",
                  body: JSON.stringify({ pin: value }),
                }),
              );
              await load();
            } catch (e) {
              setError((e as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}
export function RedPacketsPage({ walletId }: { walletId: string }) {
  type Packet = RedPacket & { link: string };
  const [mode, setMode] = useState<RedPacket["mode"]>("single"),
    [amount, setAmount] = useState(""),
    [count, setCount] = useState("2"),
    [greeting, setGreeting] = useState("恭喜发财，大吉大利"),
    [recipientId, setRecipientId] = useState("");
  const [tab, setTab] = useState("send"),
    [data, setData] = useState<{
      items: Packet[];
      received: PacketClaim[];
    } | null>(null),
    [created, setCreated] = useState<Packet | null>(null),
    [error, setError] = useState(""),
    [pin, setPin] = useState(false),
    [busy, setBusy] = useState(false);
  const guard = useRef(false);
  useEffect(() => {
    if (created) window.scrollTo(0, 0);
  }, [created]);
  const load = () =>
    request<{ items: Packet[]; received: PacketClaim[] }>("/redpackets")
      .then(setData)
      .catch((e) => setError(e.message));
  useEffect(() => {
    void load();
  }, []);
  const total = Number(amount) * (mode === "fixed" ? Number(count) : 1);
  const submit = async (value: string) => {
    if (guard.current) return;
    guard.current = true;
    setBusy(true);
    setPin(false);
    setError("");
    try {
      const p = await submitOrder<Packet>("/redpackets", {
        mode,
        amount,
        count: mode === "single" ? 1 : Number(count),
        greeting,
        recipientId: mode === "single" && recipientId ? recipientId : undefined,
        pin: value,
      });
      setCreated(p);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
      guard.current = false;
    }
  };
  return (
    <div className="app">
      <Header title="红包" />
      <WalletId id={walletId} />
      <div className="body commerce-page">
        <section className="commerce-hero packet-hero">
          <span>🧧</span>
          <div>
            <h2>把心意，装进红包</h2>
            <p>一人一份 · 余额到账 · 24 小时退回余款</p>
          </div>
        </section>
        <div className="commerce-tabs">
          {[
            ["send", "发红包"],
            ["sent", "我发出的"],
            ["received", "我收到的"],
          ].map(([key, label]) => (
            <button
              key={key}
              className={tab === key ? "active" : ""}
              onClick={() => {
                setTab(key!);
                void load();
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <ErrorText value={error} />
        {tab === "send" && (
          <>
            {created ? (
              <section className="commerce-card packet-success">
                <span className="packet-symbol">🧧</span>
                <h2>红包已准备好</h2>
                <p>{created.greeting}</p>
                <strong>
                  {money(created.total)} USDT · {created.count} 份
                </strong>
                <p className="sub">复制专属链接，发给好友或群聊</p>
                <CopyText value={created.link} />
                <a
                  className="primary button-link"
                  href={`https://t.me/share/url?url=${encodeURIComponent(created.link)}&text=${encodeURIComponent(created.greeting)}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  分享红包
                </a>
                <button
                  className="ghost"
                  onClick={() => {
                    setCreated(null);
                    setAmount("");
                  }}
                >
                  再发一个
                </button>
              </section>
            ) : (
              <form
                className="commerce-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  setError("");
                  setPin(true);
                }}
              >
                <div className="packet-modes">
                  {[
                    ["single", "单个红包", "先到先得 / 指定领取"],
                    ["fixed", "等额红包", "每人固定金额"],
                    ["random", "拼手气", "随机分配总金额"],
                  ].map(([key, label, sub]) => (
                    <button
                      type="button"
                      key={key}
                      className={mode === key ? "active" : ""}
                      onClick={() => setMode(key as RedPacket["mode"])}
                    >
                      <strong>{label}</strong>
                      <small>{sub}</small>
                    </button>
                  ))}
                </div>
                <label>
                  {mode === "fixed" ? "每份金额" : "红包总金额"}
                  <div className="commerce-input-unit">
                    <input
                      required
                      inputMode="decimal"
                      type="number"
                      min="0.01"
                      step="0.01"
                      max="1000000"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                    />
                    <span>USDT</span>
                  </div>
                </label>
                {mode !== "single" && (
                  <label>
                    红包数量
                    <input
                      required
                      type="number"
                      min="2"
                      max="100"
                      value={count}
                      onChange={(e) => setCount(e.target.value)}
                    />
                  </label>
                )}
                {mode === "single" && (
                  <label>
                    指定领取人（选填）
                    <input
                      inputMode="numeric"
                      pattern="[0-9]{1,16}"
                      placeholder="填写 Telegram ID；留空则先到先得"
                      value={recipientId}
                      onChange={(e) => setRecipientId(e.target.value)}
                    />
                  </label>
                )}
                <label>
                  祝福语
                  <input
                    maxLength={80}
                    value={greeting}
                    onChange={(e) => setGreeting(e.target.value)}
                  />
                </label>
                <div className="packet-total">
                  <small>合计扣款</small>
                  <strong>
                    {Number.isFinite(total) ? total.toFixed(2) : "0.00"}{" "}
                    <span>USDT</span>
                  </strong>
                </div>
                <p className="sub">
                  每位会员限领一份。发送人不能领取自己的红包。24
                  小时未领取的余额自动退回。
                </p>
                <button className="primary packet-button" disabled={busy}>
                  {busy ? "正在生成红包…" : "确认金额，发红包"}
                </button>
              </form>
            )}
          </>
        )}
        {tab === "sent" && (
          <section className="commerce-card">
            {!data ? (
              <p>加载中…</p>
            ) : !data.items.length ? (
              <p className="commerce-empty">还没有发出红包</p>
            ) : (
              data.items.map((p) => (
                <div className="packet-history" key={p.id}>
                  <div className="commerce-card-head">
                    <strong>🧧 {p.greeting}</strong>
                    <span>{labels[p.status]}</span>
                  </div>
                  <p>
                    {money(p.total)} USDT · 已领 {p.claimed}/{p.count} 份
                  </p>
                  <small>
                    {date(p.createdAt)}
                    {p.refunded > 0
                      ? ` · 已退回 ${money(p.refunded)} USDT`
                      : ""}
                  </small>
                  {p.status === "open" && <CopyText value={p.link} />}
                </div>
              ))
            )}
          </section>
        )}
        {tab === "received" && (
          <section className="commerce-card">
            {!data ? (
              <p>加载中…</p>
            ) : !data.received.length ? (
              <p className="commerce-empty">还没有领取红包</p>
            ) : (
              data.received.map((r) => (
                <div className="commerce-record" key={r.id}>
                  <div>
                    <strong>领取红包</strong>
                    <small>{date(r.createdAt)}</small>
                  </div>
                  <strong className="positive">+{money(r.amount)} USDT</strong>
                </div>
              ))
            )}
          </section>
        )}
      </div>
      {pin && (
        <PinPad
          title="确认发红包"
          subtitle={`将扣除 ${total.toFixed(2)} USDT，输入安全密码确认`}
          onCancel={() => setPin(false)}
          onSubmit={submit}
        />
      )}
    </div>
  );
}
export function HelpPage() {
  return (
    <div className="app">
      <Header title="钱包指南" />
      <div className="body commerce-page">
        <section className="commerce-hero">
          <span>📖</span>
          <div>
            <h2>金财钱包使用指南</h2>
            <p>常用功能，一页了解</p>
          </div>
        </section>
        {[
          [
            "如何充值？",
            "打开充值页面，将 USDT-TRC20 转入您的专属地址。到账确认后自动增加钱包余额。请先查看页面的充值通道状态。",
            "deposit",
            "打开充值",
          ],
          [
            "如何向商户付款？",
            "点击商户提供的付款链接，跳转到金财钱包机器人。核对商户、商品和金额，在聊天键盘点选 6 位支付密码，再点确认付款。",
            "security",
            "设置安全密码",
          ],
          [
            "如何发红包？",
            "选择单个、等额或拼手气红包，确认金额后生成领取链接。将链接分享给好友或群聊。每人限领一次，24 小时未领余款自动退回。",
            "redpackets",
            "打开红包",
          ],
          [
            "如何成为商户？",
            "提交商户名称、业务网站和回调地址。后台审核通过后生成 API 密钥，即可通过接口创建收款订单。",
            "merchant",
            "商户入驻",
          ],
          [
            "如何提现或查询交易？",
            "提现需要安全密码，由后台核实后处理。充值、提现、换汇、商户收付与红包均可在账单中查看。",
            "bills",
            "查看账单",
          ],
        ].map(([title, body, path, link]) => (
          <section className="commerce-card" key={title}>
            <h3>{title}</h3>
            <p>{body}</p>
            <a className="commerce-link" href={`#/${path}`}>
              {link} →
            </a>
          </section>
        ))}
      </div>
    </div>
  );
}
